import type {CSSProperties} from 'react';
import {radius} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeRouteAnchorsLook = 'route-anchor-locate' | 'route-anchor-locate-on' | 'route-anchor-apply';
export type WedgeRouteAnchorFieldVariant = 'wedge-route-anchor-field';

const ROUTE_ANCHORS_LOOKS = new Set<string>(['route-anchor-locate', 'route-anchor-locate-on', 'route-anchor-apply']);
export const isWedgeRouteAnchorsLook = (look: string): look is WedgeRouteAnchorsLook => ROUTE_ANCHORS_LOOKS.has(look);
export const isWedgeRouteAnchorFieldVariant = (variant: unknown): variant is WedgeRouteAnchorFieldVariant => variant === 'wedge-route-anchor-field';

/* Released route plan anchors form (Drive, "Plan my day"): the Start and End address fields, the use my location pill and the Update order button.
   The pill is a no-shrink inline flex line with a 6px gap, 8px by 12px of padding, a full radius, 12px semibold type on the `font` shorthand, a
   40px minimum height and no wrap. At rest it has a 1px card border, the elevated fill and the accent ink; once a device fix is held it takes the
   accent fill and the accent border. While the position is being read it is held off: a default cursor and 60% opacity. Update order is a no-shrink
   10px radius button with 9px by 14px of padding, no border, the accent fill, 13px semibold type and a 40px minimum height, held off the same way
   while an address is looked up. The ink of the pressed pill and of Update order is the on-accent variable with the source fallback and is owned by
   the caller. The field is the address entry on a flex line: it grows, may shrink to nothing and keeps the keyboard inset below it when the browser
   scrolls it into view. Each reads the exact legacy roles, and the declaration order is the order of the source. The caller owns the glyph, the
   labels and every attribute. */
const PILL_PADDING = '8px 12px', PILL_GAP = 6, PILL_HEIGHT = 40, BUSY_OPACITY = 0.6;
const APPLY_PADDING = '9px 14px', APPLY_RADIUS = 10, APPLY_HEIGHT = 40;
const FIELD_PADDING = '9px 10px', KEYBOARD_INSET = 'calc(var(--rv-kb-h, 0px) + 12px)';

export function wedgeRouteAnchorsStyle(look: WedgeRouteAnchorsLook, disabled = false): CSSProperties {
  const r = wedgeExactRole;
  if (look === 'route-anchor-apply') return {
    flex: '0 0 auto', padding: APPLY_PADDING, borderRadius: APPLY_RADIUS, border: 0, background: r('accent'), font: `600 13px ${r('font')}`,
    cursor: disabled ? 'default' : 'pointer', opacity: disabled ? BUSY_OPACITY : 1, minHeight: APPLY_HEIGHT,
  };
  const on = look === 'route-anchor-locate-on';
  return {
    flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', gap: PILL_GAP, padding: PILL_PADDING, borderRadius: 999,
    border: `1px solid ${r(on ? 'accent' : 'cardBorder')}`, background: r(on ? 'accent' : 'bgElev'), ...(on ? {} : {color: r('accent')}),
    font: `600 12px ${r('font')}`, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? BUSY_OPACITY : 1, minHeight: PILL_HEIGHT, whiteSpace: 'nowrap',
  };
}

export const wedgeRouteAnchorFieldStyle = (): CSSProperties => {
  const r = wedgeExactRole;
  return {
    flex: 1, minWidth: 0, padding: FIELD_PADDING, borderRadius: radius.md, border: `1px solid ${r('cardBorder')}`, background: r('bg'), color: r('label'),
    font: `400 14px ${r('font')}`, scrollMarginBottom: KEYBOARD_INSET,
  };
};

/* No look draws the transparent target. The pill is 40px tall on a wrapping row beside two helper lines and Update order is 40px tall beside a
   status line; a region would grow into the next line or the field above. */
export const wedgeRouteAnchorsDrawsHit = (look: string): boolean => !isWedgeRouteAnchorsLook(look);
